import {
  SimpleGrid,
  Input,
  VStack,
  Heading,
  Button,
  InputRightAddon,
  InputGroup,
  useToast,
  chakra,
  Radio,
  RadioGroup,
} from '@chakra-ui/react'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '../../../app/typedReduxHooks'
import { RootState } from '../../../app/store'
import { STATUS } from '../../../constants'
import InputField from '../../../components/InputField'
import { updateOrganizationSettingsData } from '../OrganizationSlice'
import { FundingSource, OrganizationSetting } from '../../../types/organization'

const FormWithStyle = chakra('form')

type OrgSettingSubset = Pick<
  OrganizationSetting,
  'eRxTimeout' | 'idleTimeout' | 'defaultFundingSource'
>

const OrganizationSettings = () => {
  const dispatch = useAppDispatch()
  const toast = useToast()
  const organization = useSelector((state: RootState) => state.organization?.data)
  const { updateOrganizationSetting } = useSelector(
    (state: RootState) => state.organization?.status
  )
  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
  } = useForm<OrgSettingSubset>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })

  if (!organization) {
    return null
  }

  const handleUpdate = (value: OrgSettingSubset) => {
    if (organization.id) {
      dispatch(
        updateOrganizationSettingsData({
          ...value,
          idleTimeout: value.idleTimeout * 60,
        })
      ).then((res) => {
        if (res.meta.requestStatus === 'fulfilled') {
          toast({
            title: 'Organization settings updated successfully',
            status: 'success',
            duration: 3000,
            position: 'top',
          })
        } else {
          toast({
            title: 'Failed to update organization settings, please try again.',
            status: 'error',
            duration: 3000,
            position: 'top',
          })
        }
      })
    }
  }

  const idleTimeout = organization?.settings?.idleTimeout
    ? Math.round(organization?.settings?.idleTimeout / 60)
    : 0

  return (
    <VStack alignItems="flex-start">
      <Heading as="h4" size="md" mb={4}>
        Organisation Setting
      </Heading>
      <FormWithStyle onSubmit={handleSubmit(handleUpdate)} w="100%">
        <SimpleGrid columns={[1, 2]} spacingY={[2, 4]} spacingX={[1, 20]} mb={6}>
          <InputField
            label="eRx Timeout"
            field="eRxTimeout"
            errors={errors}
            tooltipContent="0 and empty means no timeout"
            useTooltipIcon
          >
            <InputGroup>
              <Input
                type="number"
                {...register('eRxTimeout', {
                  value: organization?.settings?.eRxTimeout,
                  setValueAs: (value) => (value ? Number(value) : 0),
                  validate: (value) => {
                    if (isNaN(value)) {
                      return 'The timeout value should be a number'
                    }
                  },
                })}
              ></Input>
              <InputRightAddon children="seconds" />
            </InputGroup>
          </InputField>
          <InputField label="Inactivity Detection" field="idleTimeout" errors={errors}>
            <InputGroup>
              <Input
                type="number"
                placeholder="Inactivity Detection"
                {...register('idleTimeout', {
                  required: true,
                  value: idleTimeout,
                  setValueAs: (value) => value || 0,
                  validate: (value) => {
                    const timeout = Number(value)
                    if (isNaN(timeout)) {
                      return 'The inactivity detection timeout value should be a number'
                    }

                    if (timeout < 5 || timeout > 90) {
                      return 'Inactivity detection timeout must be between 300 seconds (5 minutes) and 5400 seconds (90 minutes), otherwise it will be 900 seconds (15 minutes) by default.'
                    }
                  },
                })}
              />
              <InputRightAddon children="minutes" />
            </InputGroup>
          </InputField>

          <Controller
            control={control}
            name="defaultFundingSource"
            render={({ field }) => (
              <InputField
                label="Default funding source"
                field="defaultFundingSource"
                errors={errors}
              >
                <RadioGroup
                  {...field}
                  display="flex"
                  gap={4}
                  defaultValue={organization?.settings?.defaultFundingSource}
                >
                  <Radio value={FundingSource.PRIVATE}>Private</Radio>
                  <Radio value={FundingSource.PBS}>PBS</Radio>
                </RadioGroup>
              </InputField>
            )}
          />
        </SimpleGrid>
        <Button
          colorScheme="blue"
          size="sm"
          type="submit"
          isLoading={updateOrganizationSetting === STATUS.PENDING}
          loadingText="Submitting"
        >
          Submit
        </Button>
      </FormWithStyle>
    </VStack>
  )
}

export default OrganizationSettings
